import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { 
  EyeIcon, 
  EyeSlashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const QuestionCard = ({ question }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const totalAnswers = question.statistics.correct_count + question.statistics.incorrect_count;
  const correctPercentage = totalAnswers > 0 
    ? Math.round((question.statistics.correct_count / totalAnswers) * 100) 
    : 0;

  const formatLastAnswered = (date) => {
    if (!date) return 'Noch nie beantwortet';
    
    const now = new Date();
    const lastAnswered = new Date(date);
    const diffInSeconds = Math.floor((now - lastAnswered) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return 'Gerade eben';
    if (diffInMinutes < 60) return `Vor ${diffInMinutes} Min.`;
    if (diffInHours < 24) return `Vor ${diffInHours} Std.`;
    if (diffInDays < 30) return `Vor ${diffInDays} Tagen`;
    
    return lastAnswered.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Funktion zum Formatieren der Tiefenangaben
  const formatDepthNumbers = (text) => {
    // Sucht nach Zahlen wie "23" und stellt die zweite Ziffer tief
    return text.replace(/\b(\d)(\d)\b(?=\.?\s+Was bedeutet das\?)/g, '$1<sub>$2</sub>');
  };

  return (
    <div className="card bg-base-100 shadow-lg ring-1 ring-base-content/5">
      <div className="card-body p-4">
        {/* Question Header with Category and Stats */}
        <div className="flex flex-col gap-3">
          {/* Category and Toggle */}
          <div className="flex items-start justify-between gap-4">
            <div className="text-xs text-base-content/60">
              {question.category_name}
            </div>
            <button
              onClick={() => setShowAnswer(!showAnswer)}
              className="btn btn-circle btn-ghost btn-sm"
            >
              {showAnswer ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Question Content */}
          <div className="prose max-w-none">
            <ReactMarkdown 
              className="text-lg mb-4"
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {formatDepthNumbers(question.question_text)}
            </ReactMarkdown>
          </div>
          {question.images.question && (
            <img 
              src={question.images.question} 
              alt="Frage" 
              className="rounded-lg max-h-64 object-contain bg-base-200"
            />
          )}

          {/* Statistics */}
          <div className="flex flex-wrap items-center -mx-1 mt-2">
            {/* Correct/Incorrect Stats */}
            <div className="px-1 py-0.5">
              <div className="flex items-center gap-1.5 text-sm">
                <CheckCircleIcon className="w-4 h-4 text-success" />
                <span className="text-base-content/70">
                  {question.statistics.correct_count}
                </span>
              </div>
            </div>
            <div className="px-1 py-0.5">
              <div className="flex items-center gap-1.5 text-sm">
                <XCircleIcon className="w-4 h-4 text-error" />
                <span className="text-base-content/70">
                  {question.statistics.incorrect_count}
                </span>
              </div>
            </div>

            {/* Success Rate */}
            {totalAnswers > 0 && (
              <div className="px-1 py-0.5">
                <div className="flex items-center gap-1.5 text-sm">
                  <ChartBarIcon className="w-4 h-4 text-primary" />
                  <span className="text-base-content/70">
                    {correctPercentage}%
                  </span>
                </div>
              </div>
            )}

            {/* Last Answered */}
            <div className="px-1 py-0.5 ml-auto">
              <div className="flex items-center gap-1.5 text-sm">
                <ClockIcon className="w-4 h-4 text-base-content/50" />
                <span className="text-base-content/50 text-xs">
                  {formatLastAnswered(question.statistics.last_answered)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Answer Section */}
        {showAnswer && (
          <>
            <div className="divider my-2"></div>
            <div>
              <div className="prose max-w-none">
                <ReactMarkdown>{question.answer}</ReactMarkdown>
              </div>
              {question.images.answer && (
                <img 
                  src={question.images.answer} 
                  alt="Antwort" 
                  className="mt-4 rounded-lg max-h-64 object-contain bg-base-200"
                />
              )}
            </div>

            {/* Explanation Toggle */}
            {question.explanation && (
              <div className="mt-4">
                <button
                  onClick={() => setShowExplanation(!showExplanation)}
                  className="btn btn-sm btn-ghost gap-2 normal-case"
                >
                  {showExplanation ? (
                    <>
                      <ChevronUpIcon className="w-4 h-4" />
                      Erklärung ausblenden
                    </>
                  ) : (
                    <>
                      <ChevronDownIcon className="w-4 h-4" />
                      Erklärung anzeigen
                    </>
                  )}
                </button>

                {showExplanation && (
                  <div className="mt-2 p-4 bg-base-200 rounded-lg prose max-w-none">
                    <ReactMarkdown>{question.explanation}</ReactMarkdown>
                    {question.images.explanation && (
                      <img 
                        src={question.images.explanation} 
                        alt="Erklärung" 
                        className="mt-4 rounded-lg max-h-64 object-contain bg-base-100"
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;
